import React from 'react';
import {
  ANALYTE_KINDS,
  checkWorkedExample,
  constantKeys,
  emptyRow,
  inputsOf,
  toKey,
  type AnalyteKind,
  type AnalyteRow,
  type WorkedExampleResult,
} from '../../utils/assayDefinition';
import { COLUMN_HELP, KIND_HELP } from '../../utils/assayHelp';
import './AssayDefinition.css';

interface AnalytesTableProps {
  rows: AnalyteRow[];
  onChange: (rows: AnalyteRow[]) => void;
  /** Every derived row with a formula needs a passing worked example (New assay page). */
  requireAllExamples?: boolean;
}

function statusText(result: WorkedExampleResult): React.ReactNode {
  switch (result.status) {
    case 'pass': return <span className="example-status pass">✓ evaluates to {result.actual}</span>;
    case 'fail': return <span className="example-status fail">✗ evaluates to {result.actual === null ? 'nothing (missing input or division by zero)' : result.actual}, expected {result.expected}</span>;
    case 'error': return <span className="example-status fail">✗ {result.message}</span>;
    case 'incomplete': return <span className="example-status incomplete">enter a value for each input and the expected result</span>;
    default: return <span className="example-status not-needed">formula unchanged since the last save</span>;
  }
}

export const AnalytesTable: React.FC<AnalytesTableProps> = ({ rows, onChange, requireAllExamples = false }) => {
  const constants = constantKeys(rows);

  const patch = (i: number, partial: Partial<AnalyteRow>) => {
    onChange(rows.map((r, idx) => (idx === i ? { ...r, ...partial } : r)));
  };
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= rows.length) return;
    const next = [...rows];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const remove = (i: number) => onChange(rows.filter((_, idx) => idx !== i));
  const add = () => onChange([...rows, emptyRow()]);

  return (
    <div>
      <table className="analytes-table">
        <thead>
          <tr>
            <th title={COLUMN_HELP.name}>Name</th>
            <th title={COLUMN_HELP.key}>Key</th>
            <th title={COLUMN_HELP.kind}>Kind</th>
            <th title={COLUMN_HELP.unit}>Unit</th>
            <th title={COLUMN_HELP.decimals}>Decimals</th>
            <th title={COLUMN_HELP.headline}>Headline</th>
            <th title={COLUMN_HELP.display}>Display</th>
            <th title={COLUMN_HELP.defaultValue}>Default / value</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={9} className="assay-muted">No analytes yet. Add one per value the assay produces or reads.</td></tr>
          )}
          {rows.map((row, i) => {
            const isDerived = row.kind === 'derived';
            const isConstant = row.kind === 'constant';
            const example = isDerived ? checkWorkedExample(rows, row, requireAllExamples) : null;
            const inputs = isDerived ? inputsOf(row, constants) : [];
            return (
              <React.Fragment key={row.id}>
                <tr className={isDerived ? 'has-detail' : undefined}>
                  <td>
                    <input
                      className="assay-input"
                      placeholder="e.g. Titrant volume"
                      value={row.name}
                      aria-label={`Analyte ${i + 1} name`}
                      onChange={e => patch(i, { name: e.target.value, key: row.keyEdited ? row.key : toKey(e.target.value) })}
                    />
                  </td>
                  <td>
                    <input
                      className="assay-input mono"
                      value={row.key}
                      aria-label={`Analyte ${i + 1} key`}
                      onChange={e => patch(i, { key: e.target.value, keyEdited: e.target.value !== toKey(row.name) })}
                    />
                  </td>
                  <td>
                    <select
                      className="assay-select"
                      value={row.kind}
                      title={`${KIND_HELP[row.kind].what} e.g. ${KIND_HELP[row.kind].example}`}
                      aria-label={`Analyte ${i + 1} kind`}
                      onChange={e => patch(i, { kind: e.target.value as AnalyteKind })}
                    >
                      {ANALYTE_KINDS.map(k => <option key={k} value={k}>{k}</option>)}
                    </select>
                  </td>
                  <td>
                    <input
                      className="assay-input narrow"
                      placeholder={row.unitNull ? '(none)' : ''}
                      value={row.unit}
                      aria-label={`Analyte ${i + 1} unit`}
                      onChange={e => patch(i, { unit: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      className="assay-input narrow"
                      type="number"
                      min={0}
                      max={12}
                      value={row.decimals}
                      aria-label={`Analyte ${i + 1} decimals`}
                      onChange={e => patch(i, { decimals: e.target.value })}
                    />
                  </td>
                  <td className="center">
                    <input type="checkbox" checked={row.headline} disabled={isConstant} aria-label={`Analyte ${i + 1} headline`} onChange={e => patch(i, { headline: e.target.checked })} />
                  </td>
                  <td className="center">
                    <input type="checkbox" checked={row.display} disabled={isConstant} aria-label={`Analyte ${i + 1} display`} onChange={e => patch(i, { display: e.target.checked })} />
                  </td>
                  <td>
                    {isConstant ? (
                      <input className="assay-input narrow" type="number" step="any" placeholder="value" value={row.value} aria-label={`Analyte ${i + 1} value`} onChange={e => patch(i, { value: e.target.value })} />
                    ) : row.kind === 'input' || row.kind === 'result' ? (
                      <input className="assay-input narrow" type="number" step="any" placeholder="default" value={row.default} aria-label={`Analyte ${i + 1} default`} onChange={e => patch(i, { default: e.target.value })} disabled={!!row.source} title={row.source ? `Copied from ${row.source.assay} / ${row.source.key} by the cross-test job` : undefined} />
                    ) : (
                      <span className="assay-muted">—</span>
                    )}
                  </td>
                  <td>
                    <span className="row-actions">
                      <button type="button" className="icon-btn" title="Move up" disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
                      <button type="button" className="icon-btn" title="Move down" disabled={i === rows.length - 1} onClick={() => move(i, 1)}>↓</button>
                      <button type="button" className="icon-btn danger" title="Remove" aria-label={`Remove analyte ${i + 1}`} onClick={() => remove(i)}>✕</button>
                    </span>
                  </td>
                </tr>
                {isDerived && (
                  <tr className="derived-detail">
                    <td colSpan={9}>
                      <div className="derived-block">
                        <div className="derived-block-row">
                          <span className="label" title={COLUMN_HELP.formula}>Formula</span>
                          <input
                            className="assay-input mono formula"
                            placeholder="e.g. (final_mg - initial_mg) / volume_l"
                            value={row.formula}
                            aria-label={`Analyte ${i + 1} formula`}
                            onChange={e => patch(i, { formula: e.target.value })}
                          />
                          <label title={COLUMN_HELP.optional}>
                            <input type="checkbox" checked={row.optional} onChange={e => patch(i, { optional: e.target.checked })} /> optional
                          </label>
                        </div>
                        <div className="derived-block-row">
                          <span className="label">Inputs</span>
                          {inputs.length > 0
                            ? <code>{inputs.join(', ')}</code>
                            : <span className="assay-muted">{row.formula.trim() ? 'none yet' : 'no formula: derived in code'}</span>}
                          {constants.length > 0 && row.formula.trim() && <span className="assay-muted">· constants available: {constants.join(', ')}</span>}
                        </div>
                        {example && example.status !== 'not-needed' && (
                          <div className="derived-block-row">
                            <span className="label" title={COLUMN_HELP.workedExample}>Worked example</span>
                            {inputs.map(key => (
                              <span key={key} className="worked-example-field">
                                <code>{key}</code> =
                                <input
                                  className="assay-input narrow"
                                  type="number"
                                  step="any"
                                  value={row.example[key] ?? ''}
                                  aria-label={`Analyte ${i + 1} example ${key}`}
                                  onChange={e => patch(i, { example: { ...row.example, [key]: e.target.value } })}
                                />
                              </span>
                            ))}
                            <span className="worked-example-field">
                              → expected
                              <input
                                className="assay-input narrow"
                                type="number"
                                step="any"
                                value={row.expected}
                                aria-label={`Analyte ${i + 1} expected result`}
                                onChange={e => patch(i, { expected: e.target.value })}
                              />
                            </span>
                            {statusText(example)}
                          </div>
                        )}
                        {example && example.status === 'not-needed' && row.formula.trim() && (
                          <div className="derived-block-row">{statusText(example)}</div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      <div className="assay-actions">
        <button type="button" className="assay-btn small" onClick={add}>+ Add analyte</button>
      </div>
      <details className="assay-help">
        <summary>What the kinds mean</summary>
        <dl>
          {ANALYTE_KINDS.map(kind => (
            <React.Fragment key={kind}>
              <dt><code>{kind}</code></dt>
              <dd>{KIND_HELP[kind].what} <span className="assay-muted">e.g. {KIND_HELP[kind].example}</span></dd>
            </React.Fragment>
          ))}
        </dl>
        <p style={{ margin: '0.5rem 0 0' }}>Hover a column heading for what it means.</p>
      </details>
    </div>
  );
};
